import type Echo from 'laravel-echo';

import { resolveApiUrl } from '@/lib/api/resolve-url';

export type EchoConnectionState =
  | 'idle'
  | 'initialized'
  | 'connecting'
  | 'connected'
  | 'unavailable'
  | 'failed'
  | 'disconnected';

let echoInstance: Echo<'reverb'> | null = null;
let initPromise: Promise<Echo<'reverb'>> | null = null;

const DEGRADED_GRACE_MS = 5_000;

let connectionState: EchoConnectionState = 'idle';
let shouldFallback = false;
let degradedTimer: ReturnType<typeof setTimeout> | null = null;

const stateListeners = new Set<() => void>();

function notify() {
  for (const cb of stateListeners) cb();
}

function isDegraded(state: EchoConnectionState): boolean {
  return state === 'unavailable' || state === 'disconnected';
}

function clearDegradedTimer() {
  if (degradedTimer) {
    clearTimeout(degradedTimer);
    degradedTimer = null;
  }
}

function setConnectionState(next: EchoConnectionState) {
  if (next === connectionState) return;
  connectionState = next;

  // Recompute fallback whenever state changes.
  clearDegradedTimer();

  let nextFallback = shouldFallback;
  if (next === 'connected') {
    nextFallback = false;
  } else if (next === 'failed') {
    nextFallback = true;
  } else if (isDegraded(next)) {
    // Give Pusher's auto-retry a grace window before flipping to fallback.
    nextFallback = false;
    degradedTimer = setTimeout(() => {
      degradedTimer = null;
      if (isDegraded(connectionState) && !shouldFallback) {
        shouldFallback = true;
        notify();
      }
    }, DEGRADED_GRACE_MS);
  }

  shouldFallback = nextFallback;
  notify();
}

export function getConnectionState(): EchoConnectionState {
  return connectionState;
}

export function getShouldFallback(): boolean {
  return shouldFallback;
}

export function subscribeConnection(cb: () => void): () => void {
  stateListeners.add(cb);
  return () => {
    stateListeners.delete(cb);
  };
}

interface PusherConnectionLike {
  bind: (
    event: 'state_change',
    handler: (states: { previous: string; current: string }) => void
  ) => void;
  state?: string;
}

interface PusherLike {
  connection: PusherConnectionLike;
}

interface ReverbConnectorLike {
  pusher: PusherLike;
}

interface AuthorizerChannel {
  name: string;
}

type AuthorizerCallback = (
  error: Error | null,
  data: { auth: string; channel_data?: string; shared_secret?: string }
) => void;

interface AuthorizerInstance {
  authorize: (socketId: string, callback: AuthorizerCallback) => void;
}

/**
 * Bearer-token authorizer for PrivateChannel subscriptions.
 *
 * The default Echo authorizer hits /broadcasting/auth with the session
 * cookie — but this app is API-only (Passport bearer tokens, no
 * session). We mirror the apiClient's auth contract by reading the
 * token from localStorage on every request, so token refreshes done
 * by apiClient.attemptTokenRefresh() are picked up on the next
 * subscription without re-creating the socket.
 *
 * Endpoint is registered by `Broadcast::routes()` in AppServiceProvider
 * with prefix=api and middleware=auth:api, so the path is the API
 * baseURL + /broadcasting/auth.
 */
function buildAuthorizer(channel: AuthorizerChannel): AuthorizerInstance {
  return {
    authorize(socketId, callback) {
      const token =
        typeof window !== 'undefined'
          ? localStorage.getItem('auth_token')
          : null;

      // gitlab-advanced-sast-exclude -- resolveApiUrl() pins every request to the configured API origin and throws on anything else.
      fetch(resolveApiUrl('/api/broadcasting/auth'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: 'application/json',
          Authorization: token ? `Bearer ${token}` : ''
        },
        body: new URLSearchParams({
          socket_id: socketId,
          channel_name: channel.name
        }).toString()
      })
        .then(async (res) => {
          if (!res.ok) {
            callback(
              new Error(`auth failed: ${res.status}`),
              {} as { auth: string }
            );
            return;
          }
          callback(null, (await res.json()) as { auth: string });
        })
        .catch((err: unknown) =>
          callback(
            err instanceof Error ? err : new Error(String(err)),
            {} as { auth: string }
          )
        );
    }
  };
}

async function createSocket(): Promise<Echo<'reverb'>> {
  const [{ default: EchoCtor }, Pusher] = await Promise.all([
    import('laravel-echo'),
    import('pusher-js').then((m) => m.default)
  ]);

  const instance = new EchoCtor({
    broadcaster: 'reverb',
    key: process.env.NEXT_PUBLIC_REVERB_APP_KEY,

    wsHost: process.env.NEXT_PUBLIC_REVERB_HOST,
    wssHost: process.env.NEXT_PUBLIC_REVERB_HOST,

    wsPort: Number(process.env.NEXT_PUBLIC_REVERB_PORT),
    wssPort: Number(process.env.NEXT_PUBLIC_REVERB_PORT),

    forceTLS: process.env.NEXT_PUBLIC_REVERB_SCHEME === 'https',
    encrypted: process.env.NEXT_PUBLIC_REVERB_SCHEME === 'https',

    enabledTransports: ['ws', 'wss'],
    disableStats: true,

    authorizer: buildAuthorizer,

    Pusher
  });

  const connector = instance.connector as unknown as ReverbConnectorLike;
  const connection = connector?.pusher?.connection;
  if (connection?.bind) {
    connection.bind('state_change', ({ current }) => {
      setConnectionState(current as EchoConnectionState);
    });
    if (typeof connection.state === 'string') {
      setConnectionState(connection.state as EchoConnectionState);
    }
  }

  return instance;
}

export async function getSocket(): Promise<Echo<'reverb'>> {
  if (echoInstance) return echoInstance;

  if (typeof window === 'undefined') {
    throw new Error('Echo cannot be used during SSR');
  }

  if (!initPromise) {
    initPromise = createSocket()
      .then((instance) => {
        echoInstance = instance;
        return instance;
      })
      .catch((err) => {
        // If the socket can't even be constructed, treat as failed so consumers fall back.
        setConnectionState('failed');
        initPromise = null;
        throw err;
      });
  }

  return initPromise;
}
